import React, { useState, useEffect } from 'react';
import { useGetLoggedUserQuery } from '../rtkq/services/userAuthApi';
import { getToken } from '../rtkq/services/localStorageService';
import { MdEmojiEvents, MdMilitaryTech } from 'react-icons/md';

// const BACKEND_BASE_URL = 'https://ft-cloud-run-200839435575.us-central1.run.app';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_SERVER_URL || "http://127.0.0.1:8000";

const Leaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { access_token } = getToken();
  
  const { data: userData, isLoading: isUserLoading } = useGetLoggedUserQuery(access_token, {
    skip: !access_token,
  });

  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (!userData) {
        setLoading(false);
        return;
      }
    
      try {
        console.log('User data:', userData);
        const userRole = userData.role?.toLowerCase() || 'student';
        const schoolName = userData.school_name || 'none'; // Use 'none' as a placeholder for no school
        const schulname = userData.school_name?.name;
        console.log("School Name:", schulname);
        const className = userData.class_name || 'none'; // Use 'none' as a placeholder for no class
    
        console.log('Complete userData:', JSON.stringify(userData, null, 2));

        // Use the correct API prefix: /api/user/
        let endpoint;
        
        if (userRole === 'teacher') {
          // For teachers, fetch based on school if available, otherwise fetch global
          endpoint = schoolName !== 'none' 
            ? `/api/user/leaderboard/school/${encodeURIComponent(schoolName)}/`
            : `/api/user/leaderboard/global/`;
        } else {
          // For students, fetch based on class if available, otherwise fetch global or by school
          if (className !== 'none' && schoolName !== 'none') {
            endpoint = `/api/user/leaderboard/class/${encodeURIComponent(schoolName)}/${encodeURIComponent(className)}/`;
          } else if (schoolName !== 'none') {
            endpoint = `/api/user/leaderboard/school/${encodeURIComponent(schoolName)}/`;
          } else {
            endpoint = `/api/user/leaderboard/global/`;
          }
        }
    
        console.log('Fetching from endpoint:', endpoint);
        
        // fetch BACKEND_BASE_URL + endpoint
        const response = await fetch(`${BACKEND_BASE_URL}${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${access_token}`,
          }
        });

        console.log("Response:", response);
    
        if (!response.ok) {
          throw new Error(`Failed to fetch leaderboard data: ${response.status}`);
        }
    
        const data = await response.json();

        console.log('Leaderboard data:', data);

        setLeaderboardData(data);
        setLoading(false);
      } catch (err) {
        console.error('Leaderboard fetch error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    if (!isUserLoading) {
      fetchLeaderboard();
    }
  }, [userData, access_token, isUserLoading]);

  // Show loading state
  if (isUserLoading || loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="text-red-600 text-center p-4">
        Error: {error}
      </div>
    );
  }

  // Show message if no user data
  if (!userData) {
    return (
      <div className="text-gray-600 text-center p-4">
        Unable to load user data. Please try again later.
      </div>
    );
  }

  // Show message if no leaderboard data
  if (!leaderboardData || leaderboardData.length === 0) {
    return (
      <div className="text-gray-600 text-center p-4">
        No leaderboard data available.
      </div>
    );
  }

  const getMedalColor = (index) => {
    switch(index) {
      case 0: return 'text-yellow-500';
      case 1: return 'text-gray-400';
      case 2: return 'text-amber-600';
      default: return 'text-gray-600';
    }
  };

  // Determine the title of the leaderboard
  const getLeaderboardTitle = () => {
    if (userData.role?.toLowerCase() === 'teacher') {
      if (userData.school_name) {
        return `School Leaderboard - ${userData.school_name}`;
      }
      return 'Global Leaderboard';
    } else {
      if (userData.class_name) {
        return `Class Leaderboard - ${userData.class_name}`;
      } else if (userData.school_name) {
        return `School Leaderboard - ${userData.school_name}`;
      }
      return 'Global Leaderboard';
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto my-8 bg-white rounded-lg shadow-lg">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-center gap-2">
          <MdEmojiEvents className="text-yellow-500" size={24} />
          <h2 className="text-2xl font-bold text-gray-800">
            {getLeaderboardTitle()}
          </h2>
        </div>
      </div>
      
      <div className="p-6">
        <div className="space-y-4">
          {leaderboardData.map((user, index) => (
            <div 
              key={user.email || index}
              className={`flex items-center justify-between p-4 rounded-lg ${
                index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
              } ${user.email === userData.email ? 'border-2 border-red-600' : ''}`}
            >
              <div className="flex items-center gap-4">
                <span className={`font-bold text-xl ${getMedalColor(index)}`}>
                  {index < 3 ? <MdMilitaryTech size={24} /> : `#${index + 1}`}
                </span>
                <div>
                  <p className="font-semibold">{`${user.first_name || ''} ${user.last_name || ''}`}</p>
                  {userData.role?.toLowerCase() === 'teacher' && user.class_name && (
                    <p className="text-sm text-gray-600">Class: {user.class_name}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-red-600">{user.rating} pts</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;